import { EPaymentState, OutcomePublic, TimeType } from '#models/outcome';
import { Product } from '#models/product';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const usePaymentContext = () => React.useContext(PaymentContext);

type DataPayment = {
  dateSelected: Date | null;
  timeSelected: TimeType | null;
  price: number;
  isPayment: boolean;
};

interface IAppState {
  paymentProduct: null | Product;
  dataPayment: DataPayment;
  currentPageState: EPaymentState | null;
}

const initState: IAppState = {
  paymentProduct: null,
  dataPayment: {
    dateSelected: null,
    timeSelected: null,
    price: 0,
    isPayment: false
  },
  currentPageState: null
};

enum AppActionTypes {
  SetPaymentProduct,
  SetDataPayment,
  SetCurrentPageState
}

interface ContextProps {
  state: IAppState;
  setPaymentProduct?: any;
  paymentEnabled?: boolean;
  schedulingEnabled?: boolean;
  setDataPayment?: any;
  setActiveState?: any;
  activeState?: string | null | undefined;
  setCurrentPageState?: any;
  outcomeData?: OutcomePublic;
}

export const PaymentContext = React.createContext<ContextProps>({ state: initState });

const stateReducer = (state = initState, action: any) => {
  switch (action.type) {
    case AppActionTypes.SetPaymentProduct:
      return {
        ...state,
        paymentProduct: action.payload
      };
    case AppActionTypes.SetDataPayment: {
      return {
        ...state,
        dataPayment: action.payload
      };
    }
    case AppActionTypes.SetCurrentPageState: {
      return {
        ...state,
        currentPageState: action.payload
      };
    }
    default:
      return state;
  }
};

export const PaymentProvider = ({
  children,
  paymentEnabled,
  schedulingEnabled,
  setActiveState,
  activeState,
  outcomeData
}: {
  children: React.ReactNode;
  paymentEnabled: boolean;
  schedulingEnabled: boolean;
  setActiveState: (activeState: string | null | undefined) => void;
  activeState: string | null | undefined;
  outcomeData?: OutcomePublic;
}) => {
  const [state, dispatch] = React.useReducer(stateReducer, initState);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (!activeState && state.paymentProduct) {
      setDataPayment({
        dateSelected: null,
        timeSelected: null,
        price: 0,
        isPayment: false
      });
      setPaymentProduct(null);
      setCurrentPageState(null);
      return;
    }
    if (
      (activeState && !state.paymentProduct) ||
      (activeState && activeState !== state.currentPageState && state.currentPageState === EPaymentState.Success)
    ) {
      navigate(location.pathname, { replace: true });
      return;
    }
    if (activeState && state.currentPageState) {
      if (activeState === state.currentPageState) {
        return;
      }
      const { currentPageState } = state;
      setDataPayment({
        ...state.dataPayment,
        dateSelected: null,
        timeSelected: null,
        isPayment: false
      });
      if (currentPageState === EPaymentState.Scheduling) {
        navigate(-1);
        return;
      }
      setCurrentPageState(activeState as EPaymentState);
    }
  }, [activeState]);

  const setPaymentProduct = React.useCallback(
    (paymentProduct: Product | null) => dispatch({ type: AppActionTypes.SetPaymentProduct, payload: paymentProduct }),
    [dispatch]
  );
  const setDataPayment = React.useCallback(
    (dataPayment: DataPayment) => dispatch({ type: AppActionTypes.SetDataPayment, payload: dataPayment }),
    [dispatch]
  );
  const setCurrentPageState = React.useCallback(
    (state: EPaymentState | null) => dispatch({ type: AppActionTypes.SetCurrentPageState, payload: state }),
    [dispatch]
  );

  const elements: ContextProps = React.useMemo(() => {
    return {
      state,
      setPaymentProduct,
      paymentEnabled,
      schedulingEnabled,
      setDataPayment,
      activeState,
      setActiveState,
      setCurrentPageState,
      outcomeData
    };
  }, [
    state,
    setPaymentProduct,
    paymentEnabled,
    schedulingEnabled,
    setDataPayment,
    activeState,
    setActiveState,
    setCurrentPageState,
    outcomeData
  ]);

  return <PaymentContext.Provider value={elements}>{children}</PaymentContext.Provider>;
};
